function initInfluencerShowcase(section) {
  if (!section || section.dataset.influencerShowcaseInit === 'true') return;
  section.dataset.influencerShowcaseInit = 'true';

  const carousel = section.querySelector('.influencer-carousel');
  let loopWidth = 0;
  let loopReady = false;
  let scrollEndTimer;

  function getOriginalCards() {
    if (!carousel) return [];
    return Array.from(carousel.querySelectorAll('.influencer-card:not([data-influencer-clone])'));
  }

  function getManagedVideos() {
    return Array.from(section.querySelectorAll('video[data-influencer-video]')).filter((video) => {
      if (video.closest('[data-influencer-clone]')) return false;
      return Boolean(video.closest('.influencer-carousel'));
    });
  }

  function prepareClone(clone) {
    clone.setAttribute('data-influencer-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');

    clone.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.removeAttribute('data-influencer-video');
      video.querySelectorAll('source').forEach((source) => {
        source.removeAttribute('src');
      });
      video.removeAttribute('src');
      video.load();
    });
  }

  function measureLoopWidth(cards) {
    if (!cards.length) return 0;
    const first = cards[0];
    const last = cards[cards.length - 1];
    return last.offsetLeft + last.offsetWidth - first.offsetLeft;
  }

  function isDesktop() {
    return window.innerWidth >= 1401;
  }

  function shouldLoop() {
    const count = getOriginalCards().length;
    if (count < 2) return false;
    if (isDesktop()) return count > 4;
    return true;
  }

  function updateLayout() {
    if (!carousel) return;
    carousel.classList.toggle('is-swiper', isDesktop() && getOriginalCards().length > 4);
  }

  function removeClones() {
    if (!carousel) return;
    carousel.querySelectorAll('[data-influencer-clone]').forEach((clone) => {
      clone.remove();
    });
    loopReady = false;
    loopWidth = 0;
  }

  function initLoop() {
    if (!carousel || loopReady || !shouldLoop()) return;

    const originals = getOriginalCards();
    if (originals.length < 2) return;

    originals.slice().reverse().forEach((card) => {
      const clone = card.cloneNode(true);
      prepareClone(clone);
      carousel.insertBefore(clone, carousel.firstChild);
    });

    originals.forEach((card) => {
      const clone = card.cloneNode(true);
      prepareClone(clone);
      carousel.appendChild(clone);
    });

    loopWidth = measureLoopWidth(originals);
    if (!loopWidth) return;

    carousel.scrollTo({ left: loopWidth, behavior: 'instant' });
    loopReady = true;
  }

  function normalizeLoopPosition() {
    if (!loopReady || !loopWidth || !carousel) return;

    const maxScroll = loopWidth * 2;
    const edgeThreshold = 4;

    if (carousel.scrollLeft >= maxScroll - edgeThreshold) {
      carousel.scrollTo({ left: carousel.scrollLeft - loopWidth, behavior: 'instant' });
    } else if (carousel.scrollLeft <= edgeThreshold) {
      carousel.scrollTo({ left: carousel.scrollLeft + loopWidth, behavior: 'instant' });
    }
  }

  function syncLoop() {
    if (!carousel) return;

    updateLayout();

    if (!shouldLoop()) {
      if (loopReady) removeClones();
      carousel.scrollTo({ left: 0, behavior: 'instant' });
      refreshVideoPlayback();
      return;
    }

    if (loopReady) {
      const originals = getOriginalCards();
      const nextWidth = measureLoopWidth(originals);
      if (nextWidth && Math.abs(nextWidth - loopWidth) > 1) {
        const ratio = loopWidth ? carousel.scrollLeft / loopWidth : 1;
        loopWidth = nextWidth;
        carousel.scrollTo({ left: ratio * loopWidth, behavior: 'instant' });
      }
    } else {
      initLoop();
    }

    refreshVideoPlayback();
  }

  function cardIsVisible(card) {
    const rect = card.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const root = carousel;
    const rootRect = root ? root.getBoundingClientRect() : {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight
    };

    const horizontalOverlap = rect.left < rootRect.right - 8 && rect.right > rootRect.left + 8;
    const verticalOverlap = rect.top < rootRect.bottom && rect.bottom > rootRect.top;
    return horizontalOverlap && verticalOverlap;
  }

  function getVisibleVideos() {
    const visible = [];
    const containers = getOriginalCards();

    containers.forEach((card) => {
      if (!cardIsVisible(card)) return;
      const video = card.querySelector('video[data-influencer-video]');
      if (video) visible.push(video);
    });

    return visible;
  }

  function updatePauseIcon(video) {
    const icon = video.closest('.influencer-video-wrap')?.querySelector('.influencer-pause-icon');
    if (!icon) return;
    icon.style.display = video.paused ? 'block' : 'none';
  }

  function playVideo(video) {
    video.muted = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        updatePauseIcon(video);
      });
    }
  }

  function toggleVideo(video) {
    if (video.paused) {
      playVideo(video);
    } else {
      video.pause();
    }
  }

  function refreshVideoPlayback() {
    const visibleVideos = new Set(getVisibleVideos());

    getManagedVideos().forEach((video) => {
      if (visibleVideos.has(video)) {
        playVideo(video);
      } else {
        video.pause();
      }

      updatePauseIcon(video);
    });
  }

  if (carousel) {
    carousel.addEventListener('scrollend', () => {
      normalizeLoopPosition();
      refreshVideoPlayback();
    });
    carousel.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
          normalizeLoopPosition();
          refreshVideoPlayback();
        }, 120);
      },
      { passive: true }
    );

    window.addEventListener('resize', syncLoop);
    requestAnimationFrame(() => {
      requestAnimationFrame(syncLoop);
    });
  } else {
    refreshVideoPlayback();
  }

  getManagedVideos().forEach((video) => {
    const wrap = video.closest('.influencer-video-wrap');

    video.addEventListener('pause', () => updatePauseIcon(video));
    video.addEventListener('play', () => updatePauseIcon(video));

    const handleToggle = (event) => {
      event.preventDefault();
      toggleVideo(video);
    };

    if (wrap) {
      wrap.addEventListener('click', handleToggle);
    } else {
      video.addEventListener('click', handleToggle);
    }
  });

  const visibilityObserver = new IntersectionObserver(
    () => {
      refreshVideoPlayback();
    },
    {
      root: carousel,
      rootMargin: '0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    }
  );

  const observedCards = getOriginalCards();

  observedCards.forEach((card) => visibilityObserver.observe(card));

  window.addEventListener('resize', refreshVideoPlayback, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshVideoPlayback();
  });

  requestAnimationFrame(refreshVideoPlayback);
}

function initAllInfluencerShowcases(root = document) {
  root.querySelectorAll('.shopify-section--influencer-showcase').forEach((section) => {
    initInfluencerShowcase(section);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initAllInfluencerShowcases();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('shopify-section--influencer-showcase')) {
    event.target.dataset.influencerShowcaseInit = 'false';
    initInfluencerShowcase(event.target);
  }
});
